import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ExternalLink, RefreshCw } from 'lucide-react';

interface PreviewAgentProps {
    agId: string;
    draftId: string | undefined;
    refresh: boolean;
    setRefreshPreview: (refresh: boolean) => void;
}

type WebsiteVersion = 'draft' | 'live';

const previewUrlStorageKey = (agId: string) => `aimdoc.agentPreviewUrl.${agId}`;

const PreviewAgent: React.FC<PreviewAgentProps> = ({ agId, draftId, refresh, setRefreshPreview }) => {
    const [previewUrl, setPreviewUrl] = useState('');
    const [websiteVersion, setWebsiteVersion] = useState<WebsiteVersion>('draft');
    const [websiteOpen, setWebsiteOpen] = useState(false);
    const [iframeKey, setIframeKey] = useState(0);

    useEffect(() => {
        if (!agId) return;
        try {
            const saved = localStorage.getItem(previewUrlStorageKey(agId));
            if (saved) setPreviewUrl(saved);
        } catch {
            // ignore storage errors
        }
    }, [agId]);

    useEffect(() => {
        if (refresh) {
            setIframeKey((prev) => prev + 1);
            setRefreshPreview(false);
        }
    }, [refresh, setRefreshPreview]);

    const reloadPreview = () => {
        setIframeKey((prev) => prev + 1);
    };

    const openWebsitePreview = (e: React.FormEvent) => {
        e.preventDefault();

        const trimmedUrl = previewUrl.trim();
        if (!trimmedUrl) {
            toast.error('Enter a website URL to preview', toastConfig);
            return;
        }

        const agentId = websiteVersion === 'live' ? agId : draftId;
        if (!agentId) {
            toast.error(
                websiteVersion === 'draft'
                    ? 'Save a draft before previewing on a website'
                    : 'Agent ID is missing',
                toastConfig
            );
            return;
        }

        try {
            localStorage.setItem(previewUrlStorageKey(agId), trimmedUrl);
        } catch {
            // ignore storage errors
        }

        const encodedUrl = encodeURIComponent(trimmedUrl);
        const appBaseUrl = import.meta.env.VITE_APP_URL;
        const previewLink = `${appBaseUrl}/agent-embed-preview/${agentId}?url=${encodedUrl}&aimdoc_preview=true`;
        window.open(previewLink, '_blank');
        setWebsiteOpen(false);
    };

    const getIframeUrl = () => {
        const baseUrl = import.meta.env.VITE_WIDGET_URL;
        return `${baseUrl}/agent-embed/${draftId}`;
    };

    return (
        <div className="flex flex-col min-w-[400px] w-1/4 max-w-[40%] h-full shrink-0">
            <TooltipProvider>
                <div className="flex flex-col border rounded-lg w-full h-full overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b shrink-0">
                        <span className="text-sm font-medium">Preview</span>
                        <Badge variant="outline" className="text-[10px] text-muted-foreground font-medium">
                            Draft
                        </Badge>
                    </div>

                    <div className="flex flex-col w-full flex-1 min-h-0 items-center overflow-hidden">
                        {draftId ? (
                            <div className="w-full h-full overflow-hidden">
                                <iframe
                                    key={iframeKey}
                                    src={getIframeUrl()}
                                    className="w-full h-full border-0"
                                    title="Agent Preview"
                                    sandbox="allow-scripts allow-same-origin allow-popups"
                                />
                            </div>
                        ) : (
                            <div className="flex flex-1 flex-col items-center justify-center gap-1 p-6 text-center">
                                <p className="text-sm font-medium">No draft yet</p>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    Save a draft to preview your agent here.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2 px-3 py-2 border-t shrink-0">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0"
                                    onClick={reloadPreview}
                                    disabled={!draftId}
                                    aria-label="Refresh preview"
                                >
                                    <RefreshCw className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Refresh preview</TooltipContent>
                        </Tooltip>

                        <Popover open={websiteOpen} onOpenChange={setWebsiteOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 h-8 gap-1.5"
                                >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    Open on website
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-80 p-4" side="top">
                                <form onSubmit={openWebsitePreview} className="space-y-3">
                                    <div className="space-y-1">
                                        <Label className="text-sm font-medium">
                                            Preview on website
                                        </Label>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            Open your agent on a page to see how it looks in context.
                                        </p>
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label htmlFor="website-preview-url" className="text-xs text-muted-foreground">
                                            Website URL
                                        </Label>
                                        <Input
                                            id="website-preview-url"
                                            value={previewUrl}
                                            onChange={(e) => setPreviewUrl(e.target.value)}
                                            placeholder="https://your-website.com"
                                            autoFocus
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <Label className="text-xs text-muted-foreground">Version</Label>
                                        <ToggleGroup
                                            type="single"
                                            value={websiteVersion}
                                            onValueChange={(value) => {
                                                if (value === 'draft' || value === 'live') {
                                                    setWebsiteVersion(value);
                                                }
                                            }}
                                            variant="outline"
                                            size="sm"
                                            className="w-full justify-stretch gap-0 rounded-md border p-0.5"
                                        >
                                            <ToggleGroupItem
                                                value="draft"
                                                className="flex-1 rounded-sm px-3 data-[state=on]:bg-muted"
                                            >
                                                Draft
                                            </ToggleGroupItem>
                                            <ToggleGroupItem
                                                value="live"
                                                className="flex-1 rounded-sm px-3 data-[state=on]:bg-muted"
                                            >
                                                Live
                                            </ToggleGroupItem>
                                        </ToggleGroup>
                                    </div>

                                    <Button type="submit" className="w-full" size="sm">
                                        Open preview
                                    </Button>
                                </form>
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
            </TooltipProvider>
        </div>
    );
};

export default PreviewAgent;
